// Plea negotiation state. Reads matter.workspace.plea (loose shape) and
// matter.workspace.bail and matter.workspace.immigrationImpact (Padilla
// flag). When no plea data, surfaces a "Run Padilla analysis" CTA.
//
// `parallelRemovalCase` (when present) is the eID of the related immigration
// matter — wrap it in an EntityLink so the attorney can jump straight to the
// removal case without losing the criminal-side context.

import { EntityLink } from "../../chrome/EntityLink";
import type { BailShape, ImmigrationImpactShape } from "../workspace-shapes";

// Heuristic: matter eIDs in DuraLex have the shape `DL-XXXXXX`. If the
// parallelRemovalCase string matches we can safely link it; otherwise it's
// a free-text label (court venue, "n/a", legacy import) and stays inline.
function looksLikeEID(s: string | undefined | null): boolean {
  return !!s && /^DL-[A-Z0-9]{5,}/i.test(s.trim());
}

interface PleaShape {
  status?: string;
  offer?: string;
  counterOffer?: string;
  acceptedDate?: string;
}

interface Props {
  plea: PleaShape | undefined;
  bail: BailShape | undefined;
  impact: ImmigrationImpactShape | undefined;
}

export function PleaStatus({ plea, bail, impact }: Props) {
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Plea & immigration impact</div>
        <div className="cc-card-sub">
          · {plea?.status ?? (impact?.padillaAdvised ? "Padilla advised" : "no plea on file")}
        </div>
      </div>
      <div style={{ padding: "14px 18px 16px" }}>
        {impact && (
          <div
            style={{
              padding: "10px 12px",
              borderRadius: 6,
              background: impact.riskLevel === "HIGH" ? "var(--red-50)" : "var(--bg)",
              border: `1px solid ${impact.riskLevel === "HIGH" ? "var(--red-600)" : "var(--line)"}`,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: impact.riskLevel === "HIGH" ? "var(--red-600)" : "var(--muted)",
                marginBottom: 4,
              }}
            >
              Padilla — immigration risk
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
              {impact.riskLevel ?? "—"}
              {impact.padillaAdvised ? ` · advised ${impact.padillaAdvised}` : ""}
            </div>
            {impact.parallelRemovalCase && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: "var(--muted)",
                }}
              >
                Parallel removal:{" "}
                {looksLikeEID(impact.parallelRemovalCase) ? (
                  <EntityLink
                    kind="matter"
                    id={impact.parallelRemovalCase}
                    eID={impact.parallelRemovalCase}
                    subtle
                  >
                    <span className="tnum-mono">{impact.parallelRemovalCase}</span>
                  </EntityLink>
                ) : (
                  impact.parallelRemovalCase
                )}
              </div>
            )}
          </div>
        )}
        {plea ? (
          <div style={{ marginBottom: 12 }}>
            {plea.offer && (
              <div style={{ marginBottom: 6 }}>
                <div
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "var(--muted)",
                  }}
                >
                  Offer
                </div>
                <div style={{ fontSize: 12.5 }}>{plea.offer}</div>
              </div>
            )}
            {plea.counterOffer && (
              <div>
                <div
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "var(--muted)",
                  }}
                >
                  Counter
                </div>
                <div style={{ fontSize: 12.5 }}>{plea.counterOffer}</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 12 }}>
            No plea offer on file.
          </div>
        )}
        {bail && (
          <div
            style={{
              fontVariantNumeric: "tabular-nums",
              fontSize: 11,
              color: "var(--muted)",
              borderTop: "1px solid var(--line-2)",
              paddingTop: 8,
            }}
          >
            Bail: {bail.type ?? "—"}
            {bail.amount != null ? ` · $${bail.amount.toLocaleString()}` : ""}
            {bail.conditions && bail.conditions.length > 0
              ? ` · ${bail.conditions.length} condition(s)`
              : ""}
          </div>
        )}
      </div>
    </div>
  );
}
